import React from "react";
import { RotateCcw } from "lucide-react";
import { Branch, Chips, TreeHeader, FieldRow } from "./FaultTreePrimitives";

/**
 * Noise / Vibration fault-tree — DEEP, 6 branching levels.
 *
 *   1. Noise / vibration / both
 *   2. Location (front/rear/left/right/under/inside)
 *   3. Character (knock / squeal / grind / drone / clunk / hum / rattle)
 *   4. When does it happen? (steering / braking / accelerating / over bumps / constant)
 *   5. Speed band (parking / low / motorway / all)
 *   6. Constant or intermittent + time since first seen + notes
 */
export default function NoiseVibrationFaultTree({
  data = {},
  onChange = () => {},
  onReset,
}) {
  const showLocation = !!data.noise_or_vib;
  const showCharacter = !!data.location && data.noise_or_vib !== "vibration";
  const showTrigger = !!data.location;
  const showSpeed = !!data.trigger || (data.location && data.noise_or_vib === "vibration");
  const showAge = !!data.speed_band;
  const showNotes = !!data.noise_or_vib;

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white overflow-hidden"
      data-testid="noise-vibration-fault-tree"
    >
      <TreeHeader
        eyebrow="Diagnostic · Fault tree"
        title="Noise / Vibration triage"
        tone="amber"
        right={
          onReset && (
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-1 text-[12.5px] text-slate-500 hover:text-slate-800"
              data-testid="noise-vib-reset"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          )
        }
      />

      <div className="p-4 space-y-3.5">
        <Branch step={1} label="Noise, vibration, or both?" testId="branch-nv-type">
          <Chips
            tone="amber"
            value={data.noise_or_vib}
            options={[
              { v: "noise", label: "Noise" },
              { v: "vibration", label: "Vibration" },
              { v: "both", label: "Both" },
            ]}
            onChange={(v) =>
              onChange({
                noise_or_vib: v,
                location: null,
                character: null,
                trigger: null,
                speed_band: null,
              })
            }
            testId="nv-type"
          />
        </Branch>

        {showLocation && (
          <Branch step={2} label="Where is it coming from?" testId="branch-nv-location">
            <Chips
              tone="amber"
              value={data.location}
              options={[
                { v: "front_left", label: "Front left" },
                { v: "front_right", label: "Front right" },
                { v: "front_centre", label: "Front centre" },
                { v: "rear_left", label: "Rear left" },
                { v: "rear_right", label: "Rear right" },
                { v: "rear_centre", label: "Rear centre" },
                { v: "underneath", label: "Underneath" },
                { v: "inside_cabin", label: "Inside cabin" },
                { v: "engine_bay", label: "Engine bay" },
                { v: "unsure", label: "Hard to tell" },
              ]}
              onChange={(v) =>
                onChange({
                  location: v,
                  character: null,
                  trigger: null,
                  speed_band: null,
                })
              }
              testId="nv-location"
            />
          </Branch>
        )}

        {showCharacter && (
          <Branch
            step={3}
            label="Character of the noise"
            subtitle="Pick whichever feels closest — exact words help the technician zero in."
            testId="branch-nv-character"
          >
            <Chips
              tone="amber"
              value={data.character}
              options={[
                { v: "knock", label: "Knock / thump" },
                { v: "clunk", label: "Clunk" },
                { v: "squeal", label: "Squeal" },
                { v: "grind", label: "Grinding" },
                { v: "drone", label: "Drone / hum" },
                { v: "rattle", label: "Rattle" },
                { v: "whine", label: "Whine" },
                { v: "tap", label: "Tapping" },
                { v: "boom", label: "Boom" },
              ]}
              onChange={(v) => onChange({ character: v })}
              testId="nv-character"
            />
          </Branch>
        )}

        {showTrigger && (
          <Branch
            step={4}
            label="When does it happen?"
            subtitle="Tick any that apply — combinations are useful."
            testId="branch-nv-trigger"
          >
            <Chips
              tone="amber"
              multi
              value={data.trigger || []}
              options={[
                { v: "on_braking", label: "When braking" },
                { v: "on_acceleration", label: "When accelerating" },
                { v: "on_steering", label: "When steering / turning" },
                { v: "over_bumps", label: "Over bumps" },
                { v: "at_idle", label: "At idle" },
                { v: "from_cold", label: "From cold" },
                { v: "constant", label: "Constant" },
              ]}
              onChange={(arr) => onChange({ trigger: arr })}
              testId="nv-trigger"
            />
          </Branch>
        )}

        {showSpeed && (
          <Branch step={5} label="Speed band" testId="branch-nv-speed">
            <Chips
              tone="amber"
              value={data.speed_band}
              options={[
                { v: "parking", label: "Parking / very low" },
                { v: "low", label: "Low (< 30 mph)" },
                { v: "mid", label: "Mid (30–60 mph)" },
                { v: "motorway", label: "Motorway (60+ mph)" },
                { v: "all_speeds", label: "All speeds" },
              ]}
              onChange={(v) => onChange({ speed_band: v })}
              testId="nv-speed"
            />
          </Branch>
        )}

        {showAge && (
          <Branch step={6} label="When did it first appear?" testId="branch-nv-age">
            <Chips
              tone="amber"
              value={data.time_since_first_seen}
              options={[
                { v: "today", label: "Today" },
                { v: "this_week", label: "This week" },
                { v: "this_month", label: "This month" },
                { v: "older", label: "1+ months ago" },
                { v: "after_repair", label: "After a recent repair" },
              ]}
              onChange={(v) => onChange({ time_since_first_seen: v })}
              testId="nv-age"
            />
          </Branch>
        )}

        {showNotes && (
          <Branch
            step={7}
            label="Customer’s own words"
            subtitle="Direct quote is gold — the technician hears the same description you did."
            testId="branch-nv-notes"
          >
            <textarea
              rows={2}
              value={data.notes || ""}
              onChange={(e) => onChange({ notes: e.target.value })}
              placeholder="e.g. ‘like a coin in a tumble dryer when I turn right at low speed’"
              className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-[15px] text-slate-900 resize-none focus:outline-none focus:border-amber-500"
              data-testid="nv-notes"
            />
          </Branch>
        )}
      </div>
    </div>
  );
}
